import { createContext, useContext, useMemo, useState } from "react";
import type { ChatTool } from "./ChatTool";
import type { PreviousConversation } from "./chatTypes";

type TranscriptSettings = {
  isGroupChat: boolean;
  currentUserId: string | undefined;
  toolsByName: Map<string, ChatTool>;
  isCurrentUserChat: boolean;
  canWrite: boolean;
  onProvideToolResult: (
    entryId: string,
    output: unknown,
    isError: boolean,
    options?: { pauseAfter?: boolean }
  ) => void;
  onRetryFromError: (errorEntryId: string) => void;
  onTypewriterAnimatingChange: (animating: boolean) => void;
  resolveFileHref?: (href: string) => string | null;
};

type ExpansionSnapshot = Pick<
  PreviousConversation,
  "expandedToolGroups" | "expandedUserActionGroups" | "expandedToolUse"
>;

export function useTranscriptExpansionState(initial?: ExpansionSnapshot) {
  const [expandedToolGroups, setExpandedToolGroups] = useState(
    () => new Set(initial?.expandedToolGroups)
  );
  const [expandedUserActionGroups, setExpandedUserActionGroups] = useState(
    () => new Set(initial?.expandedUserActionGroups)
  );
  const [expandedToolUse, setExpandedToolUse] = useState<string | null>(
    initial?.expandedToolUse ?? null
  );
  return {
    expandedToolGroups,
    setExpandedToolGroups,
    expandedUserActionGroups,
    setExpandedUserActionGroups,
    expandedToolUse,
    setExpandedToolUse,
  };
}

const TranscriptContext = createContext<TranscriptSettings | null>(null);
const TranscriptExpansionContext = createContext<ReturnType<
  typeof useTranscriptExpansionState
> | null>(null);

export function TranscriptProvider({
  value,
  expansion,
  children,
}: {
  value: TranscriptSettings;
  expansion: ReturnType<typeof useTranscriptExpansionState>;
  children: React.ReactNode;
}) {
  // Context subscriptions depend on value identity, including in uncompiled callers.
  const {
    isGroupChat,
    currentUserId,
    toolsByName,
    isCurrentUserChat,
    canWrite,
    onProvideToolResult,
    onRetryFromError,
    onTypewriterAnimatingChange,
    resolveFileHref,
  } = value;
  const settingsValue = useMemo(
    () => ({
      isGroupChat,
      currentUserId,
      toolsByName,
      isCurrentUserChat,
      canWrite,
      onProvideToolResult,
      onRetryFromError,
      onTypewriterAnimatingChange,
      resolveFileHref,
    }),
    [
      isGroupChat,
      currentUserId,
      toolsByName,
      isCurrentUserChat,
      canWrite,
      onProvideToolResult,
      onRetryFromError,
      onTypewriterAnimatingChange,
      resolveFileHref,
    ]
  );
  const {
    expandedToolGroups,
    setExpandedToolGroups,
    expandedUserActionGroups,
    setExpandedUserActionGroups,
    expandedToolUse,
    setExpandedToolUse,
  } = expansion;
  const expansionValue = useMemo(
    () => ({
      expandedToolGroups,
      setExpandedToolGroups,
      expandedUserActionGroups,
      setExpandedUserActionGroups,
      expandedToolUse,
      setExpandedToolUse,
    }),
    [
      expandedToolGroups,
      setExpandedToolGroups,
      expandedUserActionGroups,
      setExpandedUserActionGroups,
      expandedToolUse,
      setExpandedToolUse,
    ]
  );

  return (
    <TranscriptContext.Provider value={settingsValue}>
      <TranscriptExpansionContext.Provider value={expansionValue}>
        {children}
      </TranscriptExpansionContext.Provider>
    </TranscriptContext.Provider>
  );
}

export function useTranscriptSettings() {
  const value = useContext(TranscriptContext);
  if (!value) throw new Error("TranscriptProvider is required");
  return value;
}

export function useTranscriptExpansion() {
  const value = useContext(TranscriptExpansionContext);
  if (!value) throw new Error("TranscriptProvider is required");
  return value;
}
